import { useNavigate, useSearchParams } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';

import {
    ADVERT_TYPE,
    DOCUMENT_STATUS,
    IOption,
    MAINTENANCE_STATUS,
    MORTGAGE_STATUS,
    PRICE_TYPE,
    PROPERTY_TYPE,
} from 'types';
import { paths } from 'lib/constants/paths';
import { useModal } from 'hooks/use-modal';
import CheckOption from './option';
import { cn, getShowFloorInfo, getShowMaintenance } from 'lib/utils';

import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right.svg';
import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import CustomSelectPrimary from 'components/common/custom-select/primary';
import CustomInputPrimary from 'components/common/input/primary';
import ButtonPrimary from 'components/common/button/primary';
import { RenderIf } from 'components/common/render-if';
import Range from 'components/common/range';
import { MultiSelect } from 'components/common/multi-select';
import {
    useGetApiV1ClientCitiesQuery,
    useGetApiV1ClientCountriesQuery,
    useGetApiV1ClientDistrictsQuery,
    useGetApiV1ClientMetrosQuery,
    useGetApiV1ClientTargetsQuery,
} from 'service/api/allApis';
import { useLanguage } from 'hooks/use-language';

type FormValues = {
    advertType: string;
    advertOwnerType: string;
    propertyType: string;
    advertNumber: string;
    minPrice: string;
    maxPrice: string;
    minPropertyArea: string;
    maxPropertyArea: string;
    minPropertyFloor: string;
    maxPropertyFloor: string;
    maintenanceStatus: string;
    documentStatus: string;
    mortgageStatus: string;
    roomCount: string;
    priceType: string;
    countryId: string;
    cityId: string;
    districtIds: string[];
    targetIds: string[];
    metroIds: string[];
};

type Props = {
    advanced?: boolean;
};
const Filter = ({ advanced = false }: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { onClose, onOpen } = useModal();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { selectedLanguageId } = useLanguage();
    const { watch, setValue, handleSubmit, reset } = useForm<FormValues>({
        defaultValues: {
            advertType: '',
            advertOwnerType: '',
            propertyType: '',
            advertNumber: '',
            minPrice: '',
            maxPrice: '',
            minPropertyArea: '',
            maxPropertyArea: '',
            minPropertyFloor: '',
            maxPropertyFloor: '',
            maintenanceStatus: '',
            documentStatus: '',
            mortgageStatus: '',
            roomCount: '',
            priceType: '',
            countryId: '',
            cityId: '',
            districtIds: [],
            targetIds: [],
            metroIds: [],
        },
    });

    const advertType = watch('advertType');
    const countryIdValue = watch('countryId');
    const cityIdValue = watch('cityId');

    const { data: countryData, isLoading: isCountriesLoading } = useGetApiV1ClientCountriesQuery({
        isPaginated: false,
    });
    const { data: cityData, isLoading: isCitiesLoading } = useGetApiV1ClientCitiesQuery(
        { isPaginated: false, countryId: countryIdValue },
        { skip: !countryIdValue },
    );
    const { data: districtData, isLoading: isDistrictLoading } = useGetApiV1ClientDistrictsQuery(
        { isPaginated: false, cityId: cityIdValue },
        { skip: !cityIdValue },
    );
    const { data: metroData, isLoading: isMetroLoading } = useGetApiV1ClientMetrosQuery(
        { isPaginated: false, cityId: cityIdValue },
        { skip: !cityIdValue },
    );
    const { data: targetData, isLoading: isTargetLoading } = useGetApiV1ClientTargetsQuery(
        { isPaginated: false, cityId: cityIdValue },
        { skip: !cityIdValue },
    );

    const countryOptions = useMemo(() => {
        if (!countryData?.items) return [];
        return countryData.items.map((item) => {
            const label =
                item.countryDictionaryGetAllDtos?.find(
                    (item) => item.languageId === selectedLanguageId,
                )?.name || item.name!;
            return { value: item.id!, label };
        });
    }, [countryData, selectedLanguageId]);

    const cityOptions = useMemo(() => {
        if (!cityData?.items) return [];
        return cityData.items.map((item) => {
            const label =
                item.cityDictionaryGetAllDtos?.find(
                    (item) => item.languageId === selectedLanguageId,
                )?.name || item.name!;
            return { value: item.id!, label };
        });
    }, [cityData, selectedLanguageId]);

    const districtOptions = useMemo(() => {
        if (!districtData?.items) return [];
        return districtData.items.map((item) => {
            const label =
                item.districtDictionaryGetAllDtos?.find(
                    (item) => item.languageId === selectedLanguageId,
                )?.name || item.name!;
            return { value: item.id!, label };
        });
    }, [districtData, selectedLanguageId]);

    const metroOptions = useMemo(() => {
        if (!metroData?.items) return [];
        return metroData.items.map((item) => {
            const label =
                item.metroDictionaryGetAllDtos?.find(
                    (item) => item.languageId === selectedLanguageId,
                )?.name || item.name!;
            return { value: item.id!, label };
        });
    }, [metroData, selectedLanguageId]);

    const targetOptions = useMemo(() => {
        if (!targetData?.items) return [];
        return targetData.items.map((item) => {
            const label =
                item.targetDictionaryGetAllDtos?.find(
                    (item) => item.languageId === selectedLanguageId,
                )?.name || item.name!;
            return { value: item.id!, label };
        });
    }, [targetData, selectedLanguageId]);

    const filterOptions: (IOption & {
        type: 'checkbox' | 'simple';
        key?: string;
    })[] = [];

    const showDocumentStatus =
        (watch('advertType') as ADVERT_TYPE) === ADVERT_TYPE.SALE || !watch('advertType');
    if (showDocumentStatus) {
        filterOptions.unshift({
            value: String(DOCUMENT_STATUS.YES),
            key: 'documentStatus',
            label: t('advert.with-document'),
            type: 'checkbox',
        });
    }
    const showMortgageStatus =
        (watch('advertType') as ADVERT_TYPE) === ADVERT_TYPE.SALE || !watch('advertType');
    if (showMortgageStatus) {
        filterOptions.unshift({
            value: String(MORTGAGE_STATUS.YES),
            key: 'mortgageStatus',
            label: t('advert.with-mortgage'),
            type: 'checkbox',
        });
    }

    const showMaintenanceFilter =
        getShowMaintenance(watch('propertyType') as PROPERTY_TYPE) || !watch('propertyType');
    if (showMaintenanceFilter) {
        filterOptions.unshift(
            {
                value: String(MAINTENANCE_STATUS.FULL),
                key: 'maintenanceStatus',
                label: t('advert.full-repair'),
                type: 'checkbox',
            },
            {
                value: String(MAINTENANCE_STATUS.AVERAGE),
                key: 'maintenanceStatus',
                label: t('advert.average-repair'),
                type: 'checkbox',
            },
            {
                value: String(MAINTENANCE_STATUS.BAD),
                key: 'maintenanceStatus',
                label: t('advert.without-repair'),
                type: 'checkbox',
            },
        );
    }

    const showFloorInfo =
        getShowFloorInfo(watch('propertyType') as PROPERTY_TYPE) || !watch('propertyType');

    const advertTypeOptions = [
        {
            value: '',
            label: t('general.all'),
        },
        {
            value: String(ADVERT_TYPE.RENT),
            label: t('advert.rent'),
        },
        {
            value: String(ADVERT_TYPE.SALE),
            label: t('advert.sale'),
        },
    ];
    const propertyTypeOptions = [
        {
            value: '',
            label: t('general.all'),
        },
        {
            value: String(PROPERTY_TYPE.NEW_BUILDING),
            label: t('advert.new-building'),
        },
        {
            value: String(PROPERTY_TYPE.OLD_BUILDING),
            label: t('advert.old-building'),
        },
        {
            value: String(PROPERTY_TYPE.COMMERCIAL),
            label: t('advert.commercial'),
        },
        {
            value: String(PROPERTY_TYPE.OFFICE),
            label: t('advert.office'),
        },
        {
            value: String(PROPERTY_TYPE.GARAGE),
            label: t('advert.garage'),
        },
        {
            value: String(PROPERTY_TYPE.YARD_HOUSE),
            label: t('advert.yard-house'),
        },
        {
            value: String(PROPERTY_TYPE.LAND),
            label: t('advert.land'),
        },
    ];
    const roomCountOptions = [
        {
            value: '',
            label: t('general.all'),
        },
        {
            value: '1',
            label: t('advert.room-with-count', { count: 1 }),
        },
        {
            value: '2',
            label: t('advert.room-with-count', { count: 2 }),
        },
        {
            value: '3',
            label: t('advert.room-with-count', { count: 3 }),
        },
        {
            value: '4',
            label: t('advert.room-with-count', { count: 4 }),
        },
        {
            value: '5',
            label: t('advert.room-with-count', { count: 5 }),
        },
        {
            value: '6',
            label: t('advert.room-with-count', { count: 6 }),
        },
    ];
    const priceTypeOptions = [
        { value: String(PRICE_TYPE.MONTHLY), label: t('advert.monthly') },
        { value: String(PRICE_TYPE.DAILY), label: t('advert.daily') },
    ];

    const onSubmit: SubmitHandler<FormValues> = (values) => {
        onClose();
        if (values.advertNumber) {
            return navigate(paths.advert.view(values.advertNumber));
        }
        const searchParams = new URLSearchParams();
        Object.entries(values).forEach(([key, value]) => {
            if (!value) return;
            if (Array.isArray(value)) {
                value.forEach((item) => {
                    searchParams.append(key, item);
                });
            } else {
                searchParams.append(key, value);
            }
        });

        navigate(paths.advert.list + '?' + searchParams.toString());
    };

    const onReset = () => {
        reset();
        const searchParams = new URLSearchParams();
        setSearchParams(searchParams);
    };

    useEffect(() => {
        reset({
            advertType:
                searchParams.get('advertType') || (!advanced ? String(ADVERT_TYPE.SALE) : ''),
            advertOwnerType: searchParams.get('advertOwnerType') || '',
            propertyType: searchParams.get('propertyType') || '',
            advertNumber: searchParams.get('advertNumber') || '',
            minPrice: searchParams.get('minPrice') || '',
            maxPrice: searchParams.get('maxPrice') || '',
            minPropertyArea: searchParams.get('minPropertyArea') || '',
            maxPropertyArea: searchParams.get('maxPropertyArea') || '',
            minPropertyFloor: searchParams.get('minPropertyFloor') || '',
            maxPropertyFloor: searchParams.get('maxPropertyFloor') || '',
            maintenanceStatus: searchParams.get('maintenanceStatus') || '',
            documentStatus: searchParams.get('documentStatus') || '',
            mortgageStatus: searchParams.get('mortgageStatus') || '',
            roomCount: searchParams.get('roomCount') || '',
            countryId: searchParams.get('countryId') || '',
            cityId: searchParams.get('cityId') || '',
            districtIds: searchParams.getAll('districtIds'),
            targetIds: searchParams.getAll('targetIds'),
            metroIds: searchParams.getAll('metroIds'),
        });
    }, [searchParams.toString()]);
    console.log('targetOptions.length', targetOptions.length);

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="md:px-5 sm:px-0">
            <div className="flex flex-col md:flex-row">
                <div
                    className={cn(
                        'w-full pb-3.5',
                        advanced && 'md:pb-[30px] md:max-w-[350px] md:pr-4 md:pt-[22px]',
                    )}
                >
                    <RenderIf condition={!advanced}>
                        <div className="border-b mb-4 border-dark-20 flex justify-between px-5 text-[#757374] font-semibold tracking-[0.64px]">
                            <button
                                type="button"
                                className={cn(
                                    'grow pb-5 md:py-5 leading-[19px] border-b-2 border-transparent hover:bg-primary/10 duration-100',
                                    advertType === String(ADVERT_TYPE.SALE) &&
                                        'border-primary text-primary',
                                )}
                                onClick={() => setValue('advertType', String(ADVERT_TYPE.SALE))}
                            >
                                {t('general.sale')}
                            </button>
                            <button
                                type="button"
                                className={cn(
                                    'grow pb-5 md:py-5 leading-[19px] border-b-2 border-transparent hover:bg-primary/10 duration-100',
                                    advertType === String(ADVERT_TYPE.RENT) &&
                                        'border-primary text-primary',
                                )}
                                onClick={() => setValue('advertType', String(ADVERT_TYPE.RENT))}
                            >
                                {t('general.rent')}
                            </button>
                        </div>
                    </RenderIf>
                    <RenderIf condition={!advanced}>
                        <div className="mt-[40px]" />
                    </RenderIf>
                    <RenderIf condition={advanced}>
                        <CustomSelectPrimary
                            options={advertTypeOptions}
                            placeholder={t('advert.purpose')}
                            className={cn(
                                !!cityIdValue && (!!districtOptions.length || !!metroOptions.length)
                                    ? 'mb-4'
                                    : 'mb-4 lg:mb-7',
                            )}
                            value={watch('advertType')}
                            onChange={(value) => setValue('advertType', value)}
                        />
                    </RenderIf>
                    <RenderIf condition={advanced && advertType !== String(ADVERT_TYPE.SALE)}>
                        <CustomSelectPrimary
                            options={priceTypeOptions}
                            placeholder={t('advert.price-type')}
                            className={cn(
                                'mb-4',
                                !!cityIdValue && !targetOptions.length && 'lg:mb-7',
                            )}
                            value={watch('priceType')}
                            onChange={(value) => setValue('priceType', value)}
                        />
                    </RenderIf>
                    <div className={cn('flex flex-col', !advanced && 'px-5')}>
                        <CustomSelectPrimary
                            options={propertyTypeOptions}
                            placeholder={t('advert.property-type')}
                            value={watch('propertyType') || undefined}
                            onChange={(value) => setValue('propertyType', value)}
                            className={cn(
                                'mb-4',
                                advanced &&
                                    advertType === String(ADVERT_TYPE.RENT) &&
                                    !!cityIdValue &&
                                    !!metroOptions.length &&
                                    'lg:mb-7',
                            )}
                        />
                        <RenderIf condition={watch('propertyType') !== String(PROPERTY_TYPE.LAND)}>
                            <CustomSelectPrimary
                                options={roomCountOptions}
                                placeholder={t('advert.room-count')}
                                value={advanced ? watch('roomCount') || undefined : undefined}
                                onChange={(value) => setValue('roomCount', value)}
                                className={cn('mb-4')}
                            />
                        </RenderIf>
                        <CustomInputPrimary
                            placeholder={t('advert.advert-number')}
                            value={watch('advertNumber')}
                            onChange={(value) => setValue('advertNumber', value)}
                        />
                    </div>
                </div>
                <RenderIf condition={advanced}>
                    <div className="w-full">
                        <div className="grid lg:grid-cols-2 md:pt-[22px] mb-2 gap-x-5 gap-y-4">
                            <CustomSelectPrimary
                                options={countryOptions}
                                loading={isCountriesLoading}
                                value={watch('countryId')}
                                placeholder={t('general.country')}
                                onChange={(value) => {
                                    setValue('countryId', value);
                                    setValue('cityId', '');
                                }}
                            />
                            <CustomSelectPrimary
                                disabled={!countryIdValue}
                                loading={isCitiesLoading}
                                options={cityOptions}
                                value={cityIdValue}
                                placeholder={t('general.city')}
                                onChange={(value) => setValue('cityId', value)}
                            />

                            <RenderIf condition={!!cityIdValue && !!districtOptions.length}>
                                <MultiSelect
                                    options={districtOptions}
                                    isLoading={isDistrictLoading}
                                    defaultValue={watch('districtIds')}
                                    onValueChange={(values) => {
                                        setValue('districtIds', values);
                                    }}
                                    placeholder={t('general.district')}
                                    variant="default"
                                    animation={0}
                                />
                            </RenderIf>
                            <RenderIf condition={!!cityIdValue && !!metroOptions.length}>
                                <MultiSelect
                                    options={metroOptions}
                                    isLoading={isMetroLoading}
                                    defaultValue={watch('metroIds')}
                                    onValueChange={(values) => {
                                        setValue('metroIds', values);
                                    }}
                                    placeholder={t('general.metro')}
                                    variant="default"
                                    animation={0}
                                />
                            </RenderIf>
                            <RenderIf condition={!!cityIdValue && !!targetOptions.length}>
                                <MultiSelect
                                    options={targetOptions}
                                    isLoading={isTargetLoading}
                                    defaultValue={watch('targetIds')}
                                    onValueChange={(values) => {
                                        setValue('targetIds', values);
                                    }}
                                    placeholder={t('general.target')}
                                    variant="default"
                                    animation={0}
                                />
                            </RenderIf>
                        </div>
                        <div
                            className={cn(
                                'w-full text-sm grid grid-cols-1 sm:grid-cols-2  gap-x-5 gap-y-3 md:grid-cols-3',
                            )}
                        >
                            <Range
                                minValue={watch('minPrice')}
                                maxValue={watch('maxPrice')}
                                onChange={({ min, max }) => {
                                    setValue('minPrice', min);
                                    setValue('maxPrice', max);
                                }}
                                label={t('advert.price')}
                            />
                            <Range
                                minValue={watch('minPropertyArea')}
                                maxValue={watch('maxPropertyArea')}
                                onChange={({ min, max }) => {
                                    setValue('minPropertyArea', min);
                                    setValue('maxPropertyArea', max);
                                }}
                                label={t('advert.property-area')}
                            />
                            <RenderIf condition={showFloorInfo}>
                                <Range
                                    minValue={watch('minPropertyFloor')}
                                    maxValue={watch('maxPropertyFloor')}
                                    onChange={({ min, max }) => {
                                        setValue('minPropertyFloor', min);
                                        setValue('maxPropertyFloor', max);
                                    }}
                                    label={t('advert.property-floor')}
                                />
                            </RenderIf>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-2.5 lg:gap-x-5 gap-y-4 mt-5 text-[15px] lg:text-base">
                            {filterOptions.map((option) => (
                                <CheckOption
                                    key={option.value}
                                    option={option}
                                    value={option.key ? watch(option.key as any) : undefined}
                                    onChange={(value) => {
                                        setValue(option.key! as any, value);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </RenderIf>
            </div>
            <RenderIf condition={!advanced}>
                <button
                    type="button"
                    onClick={() => onOpen(`advanced-filter`)}
                    className="hidden md:flex items-center gap-x-4 font-bold text-brend hover:opacity-80 duration-100 cursor-pointer pl-5"
                >
                    <FilterIcon />
                    {t('filter.advanced-search')}
                </button>
            </RenderIf>
            <div
                className={cn('flex justify-between items-center mt-20', !advanced && 'px-5 mt-8')}
            >
                <RenderIf condition={advanced}>
                    <button
                        type="button"
                        onClick={onClose}
                        className="mr-4 md:mr-0 flex items-center gap-x-4 font-bold text-brend hover:opacity-80 duration-100 cursor-pointer"
                    >
                        {t('filter.hide')}
                        <ArrowRightIcon className="fill-brend w-[10px] h-[10px]" />
                    </button>
                </RenderIf>
                <div
                    className={cn(
                        'flex w-full justify-between md:justify-start md:gap-x-10',
                        advanced && 'md:w-fit',
                    )}
                >
                    <RenderIf condition={advanced}>
                        <button
                            type="button"
                            onClick={onReset}
                            className="flex items-center gap-x-4 font-bold text-brend hover:opacity-80 duration-100 cursor-pointer"
                        >
                            {t('filter.reset')}
                        </button>
                    </RenderIf>
                    <div className={cn(!advanced && 'w-full')}>
                        <ButtonPrimary
                            type="submit"
                            text={t('filter.search')}
                            icon={<SearchIcon />}
                            className={cn(advanced ? 'w-32 md:w-56 ' : 'w-full')}
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default Filter;
